import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import TwoColumnLayout from 'layouts/two-column-layout/two-column-layout'
import { useLazyListTripsQuery } from 'store/trips/trips.api'
import { useDispatch, useSelector } from 'react-redux'
import {
  setSelectedTrip,
  clearSelectedTrip,
} from 'store/trips/trips.slice'
import { RootState } from 'store'
import { ChangeEvent } from 'react'
import { tripTabs } from 'pages/trip/trip.constants'
import CarrierTripSelection from './carrier-trip-selection/carrier-trip-selection.component'
import CarrierTripDetails from './carrier-trip-details/carrier-trip-details.component'

const CarrierTripPage = () => {
  const dispatch = useDispatch()
  const [tabsValue, setTabsValue] = useState(0)

  const selectedTrip = useSelector(
    (state: RootState) => state.trips.selectedTrip
  )

  const [listActiveTrips, { data: activeTrips }] = useLazyListTripsQuery()
  const [listArchivedTrips, { data: archivedTrips }] = useLazyListTripsQuery()

  const fetchActiveTrips = useCallback(async () => {
    try {
      await listActiveTrips({
        page: 0,
        size: 10,
        sortBy: 'tripId',
        status: 'ACTIVE',
        tripVariant: 'carrier',
      }).unwrap()
    } catch (error) {
      console.error('Failed to fetch active trips: ', error)
    }
  }, [listActiveTrips])

  const fetchArchivedTrips = useCallback(async () => {
    try {
      await listArchivedTrips({
        page: 0,
        size: 10,
        sortBy: 'tripId',
        status: 'ARCHIVED',
        tripVariant: 'carrier',
      }).unwrap()
    } catch (error) {
      console.error('Failed to fetch archived trips: ', error)
    }
  }, [listArchivedTrips])

  const handlePagination = async (
    _event: ChangeEvent<unknown>,
    page: number
  ) => {
    try {
      if (tabsValue === 0) {
        await listActiveTrips({
          page: page - 1,
          size: 10,
          sortBy: 'tripId',
          status: 'ACTIVE',
          tripVariant: 'carrier',
        }).unwrap()
      } else if (tabsValue === 1) {
        await listArchivedTrips({
          page: page - 1,
          size: 10,
          sortBy: 'tripId',
          status: 'ARCHIVED',
          tripVariant: 'carrier',
        }).unwrap()
      }
    } catch (error) {
      console.error('Failed to fetch trips: ', error)
    }
  }

  const refetch = useCallback(() => {
    if (tabsValue === 0) {
      fetchActiveTrips()
    } else {
      fetchArchivedTrips()
    }
  }, [tabsValue, fetchActiveTrips, fetchArchivedTrips])

  const refetchAll = useCallback(() => {
    void fetchActiveTrips()
    void fetchArchivedTrips()
  }, [fetchActiveTrips, fetchArchivedTrips])

  // Only set for create — blocks keep-selection while the new trip is being selected.
  const selectingNewestAfterCreateRef = useRef(false)

  const handleTripCreated = useCallback(async () => {
    setTabsValue(0)
    selectingNewestAfterCreateRef.current = true
    try {
      const result = await listActiveTrips({
        page: 0,
        size: 10,
        sortBy: 'tripId',
        status: 'ACTIVE',
        tripVariant: 'carrier',
      }).unwrap()

      if (result?.trips?.length) {
        dispatch(setSelectedTrip({ trip: result.trips[0] }))
      }
    } catch (error) {
      console.error('Failed to fetch active trips: ', error)
    } finally {
      selectingNewestAfterCreateRef.current = false
    }
  }, [listActiveTrips, dispatch])

  useEffect(() => {
    if (tabsValue === 0) {
      fetchActiveTrips()
    } else if (tabsValue === 1) {
      fetchArchivedTrips()
    }
  }, [tabsValue, fetchActiveTrips, fetchArchivedTrips])

  // Prefetch only inactive tab for counts — never overwrite the active tab's page.
  useEffect(() => {
    if (tabsValue !== 0) {
      void listActiveTrips({
        page: 0,
        size: 10,
        sortBy: 'tripId',
        status: 'ACTIVE',
        tripVariant: 'carrier',
      })
    }
    if (tabsValue !== 1) {
      void listArchivedTrips({
        page: 0,
        size: 10,
        sortBy: 'tripId',
        status: 'ARCHIVED',
        tripVariant: 'carrier',
      })
    }
  }, [tabsValue, listActiveTrips, listArchivedTrips])

  const selectedTripRef = useRef(selectedTrip)
  selectedTripRef.current = selectedTrip

  // Edit / status / end / pagination: keep the user's selected trip after refetch.
  // Create: skip this while selectingNewestAfterCreateRef is set (handled in handleTripCreated).
  useEffect(() => {
    if (selectingNewestAfterCreateRef.current) return

    const trips = (tabsValue === 0 ? activeTrips : archivedTrips)?.trips
    if (!trips?.length) {
      if (selectedTripRef.current) {
        dispatch(clearSelectedTrip())
      }
      return
    }

    const current = selectedTripRef.current
    if (current) {
      const updated = trips.find((trip) => trip.tripId === current.tripId)
      if (updated) {
        dispatch(setSelectedTrip({ trip: updated }))
        return
      }
    }

    dispatch(setSelectedTrip({ trip: trips[0] }))
  }, [dispatch, activeTrips, archivedTrips, tabsValue])

  const trips = tabsValue === 0 ? activeTrips : archivedTrips
  const page = trips?.page

  const tabsWithCounts = useMemo(() => {
    const activeCount =
      activeTrips?.page?.totalElements ?? activeTrips?.trips?.length ?? 0
    const archiveCount =
      archivedTrips?.page?.totalElements ?? archivedTrips?.trips?.length ?? 0

    return tripTabs.map((tab) => {
      if (tab.id === 'active') {
        return { ...tab, label: `Active (${activeCount})` }
      }
      if (tab.id === 'archive') {
        return { ...tab, label: `Completed (${archiveCount})` }
      }
      return tab
    })
  }, [
    activeTrips?.page?.totalElements,
    activeTrips?.trips?.length,
    archivedTrips?.page?.totalElements,
    archivedTrips?.trips?.length,
  ])

  return (
    <TwoColumnLayout>
      <CarrierTripSelection
        trips={trips?.trips}
        tabsValue={tabsValue}
        setTabsValue={setTabsValue}
        page={page}
        handlePagination={handlePagination}
        onTripCreated={handleTripCreated}
        onTripsChanged={refetchAll}
        tabs={tabsWithCounts}
      />
      <CarrierTripDetails trip={selectedTrip} refetch={refetch} />
    </TwoColumnLayout>
  )
}

export default CarrierTripPage
